import { motion } from 'framer-motion';
import { useCartStore } from '@/stores/cartStore';
import type { Product } from '@/types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import SectionTitle from '@/components/ui/SectionTitle';
import ProductCard from '@/components/ui/ProductCard';

type Variety = 'premium' | 'tradicional';

interface ProductWithVariety extends Product {
  variety: Variety;
  size: string;
}

const products: ProductWithVariety[] = [
  {
    id: 'premium-300g',
    name: 'Café Premium - 300g',
    variety: 'premium',
    size: '300g',
    description: 'Café Premium. Aroma elegante, proceso 100% artesanal. Presentación de 300g ideal para descubrir el sabor real.',
    image: '/images/presentaciones/premium300g.png',
  },
  {
    id: 'premium-500g',
    name: 'Café Premium - 500g',
    variety: 'premium',
    size: '500g',
    description: 'Café Premium. Aroma elegante, proceso 100% artesanal. Presentación de 500g para disfrutar más.',
    image: '/images/presentaciones/premiun_mediokg.png',
  },
  {
    id: 'premium-1kg',
    name: 'Café Premium - 1kg',
    variety: 'premium',
    size: '1kg',
    description: 'Café Premium. Aroma elegante, proceso 100% artesanal. El formato familiar para los verdaderos amantes del café.',
    image: '/images/presentaciones/premiun1kg.png',
  },
  {
    id: 'tradicional-300g',
    name: 'Café Tradicional - 300g',
    variety: 'tradicional',
    size: '300g',
    description: 'Nuestro café tradicional. un sabor característico y aroma reconfortante. Presentación de 300g.',
    image: '/images/presentaciones/tradicional_300g.png',
  },
  {
    id: 'tradicional-500g',
    name: 'Café Tradicional - 500g',
    variety: 'tradicional',
    size: '500g',
    description: 'Café Tradicional Los Sauces. El sabor de siempre en presentación de 500g para compartir momentos especiales.',
    image: '/images/presentaciones/tradicional_mediokg.png',
  },
  {
    id: 'tradicional-1kg',
    name: 'Café Tradicional - 1kg',
    variety: 'tradicional',
    size: '1kg',
    description: 'Café Tradicional en formato familiar de 1kg. Para que nunca te falte el mejor café en tu mesa.',
    image: '/images/presentaciones/tradicional1kg.png',
  },
];

const varietyConfig = {
  premium: {
    label: 'Café Premium',
    color: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    textColor: 'text-blue-900',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  tradicional: {
    label: 'Café Tradicional',
    color: 'bg-amber-700',
    hoverColor: 'hover:bg-amber-800',
    textColor: 'text-amber-900',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
};

// Removed unused animation configs (container, child) to avoid linter warnings

export default function BuySection() {
  const { t } = useTranslation();
  const addItem = useCartStore((s) => s.addItem);
  const [selectedVariety, setSelectedVariety] = useState<Variety>('premium');

  const handleAdd = (product: Product) => {
    addItem({ id: product.id, name: product.name, description: product.description, image: product.image });
  };

  const filteredProducts = products.filter((p) => p.variety === selectedVariety);
  const config = varietyConfig[selectedVariety];

  return (
    <section id="buy" className="relative py-20 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle title={t('buy.title')} />

        {/* Selector de variedad */}
        <motion.div
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex bg-gray-100 p-1.5 rounded-full shadow-inner">
            {(Object.keys(varietyConfig) as Variety[]).map((variety) => {
              const isActive = selectedVariety === variety;
              const vConfig = varietyConfig[variety];
              return (
                <motion.button
                  key={variety}
                  onClick={() => setSelectedVariety(variety)}
                  className={`relative px-6 py-3 rounded-full font-display text-sm md:text-base transition-colors duration-300 ${
                    isActive ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  whileHover={{ scale: isActive ? 1 : 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeVariety"
                      className={`absolute inset-0 ${vConfig.color} rounded-full shadow-md`}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{vConfig.label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Descripción de la variedad seleccionada */}
        <div
          className={`text-center max-w-2xl mx-auto mb-10 p-4 rounded-xl ${config.bgColor} border ${config.borderColor} transition-all duration-300`}
        >
          <p className={`text-sm md:text-base ${config.textColor}`}>
            {selectedVariety === 'premium'
              ? 'Café Arábica 100% con tostión media. Descubre notas florales, cítricas y un cuerpo suave.'
              : 'Nuestro clásico café tradicional. Sabor equilibrado, aroma reconfortante y cuerpo medio.'}
          </p>
        </div>

        <div key={selectedVariety} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} onAddToCart={handleAdd} />
          ))}
        </div>
      </div>
    </section>
  );
}
